/**
 * attendanceFlagAdapter — le corps de `POST /api/v1/live-sessions/{id}/new-attendance-flag`
 * et les créneaux de `GET /api/v1/user/recovery-possibilities`.
 *
 * Port littéral de deux écrans legacy :
 *  - `AbsenceFormComponent` (`onSubmit`) — `{ type, late_minutes?, occurrence_date,
 *    recovery_occurrence_id?, student_email }`, `late_minutes` en CHAÎNE comme ses options ;
 *  - `RecoverLiveSessionComponent` — les paramètres de l'appel, la règle « Meilleur groupe »
 *    (`is_usual_tutor || (students_count === 1 && students_same_class)`), la table des motifs
 *    de refus (`cannotRecoverReasons`) et le quota de l'encadré « Absences » (`AbsenceQuotaBadge`).
 *
 * API-aware : le design system n'en voit rien, il reçoit des `UIParentRecoverySlot` et un
 * `UIAbsenceQuota`.
 */
import moment from "moment"

import { toUITutor, type ApiLiveOccurrenceTutor } from "../LiveSessions/liveSessionListAdapter"
import {
    type UIAbsenceNotRecoveredReason,
    type UIAbsenceQuota,
    type UILiveSession,
    type UIParentRecoverySlot,
} from "../../../ui/types"

/**
 * Le format que le serveur compare (`Rule::in` sur `getLocalNextOccurrences`), et celui que le
 * legacy envoie déjà : `config('timezone.format')` vaut `Y-m-d H:i`. Les deux payloads qui
 * portent une date d'occurrence la sérialisent ainsi ; on la renormalise quand même pour qu'une
 * date ISO venue d'ailleurs ne parte pas en 422 silencieux.
 */
function occurrenceDateParam(date: string): string {
    const parsed = moment(date)
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : date
}

/** Corps de `new-attendance-flag`. `tutor_absent` existe côté serveur mais n'est pas un geste parent. */
export type ApiAttendanceFlagBody = {
    type: "absent" | "late"
    /** Chaîne, comme les options du formulaire legacy (`"5"` … `"30"`). */
    late_minutes?: string
    occurrence_date: string
    /** `null` = « je choisirai plus tard », ou une absence qui ne se rattrape pas. */
    recovery_occurrence_id?: string | number | null
    student_email?: string
}

export type AttendanceFlagSession = Pick<UILiveSession, "date">

/**
 * Prévenir d'une absence. `recovery_occurrence_id` est TOUJOURS présent, à `null` quand aucun
 * créneau n'est choisi : c'est ce que fait le champ legacy, qui se remet à `null` à chaque
 * réponse de `recovery-possibilities`.
 */
export function absenceFlagBody(args: {
    session: AttendanceFlagSession
    recoveryOccurrenceId?: string | number | null
    studentEmail?: string | null
}): ApiAttendanceFlagBody {
    return {
        type: "absent",
        occurrence_date: occurrenceDateParam(args.session.date),
        recovery_occurrence_id: args.recoveryOccurrenceId ?? null,
        ...(args.studentEmail ? { student_email: args.studentEmail } : {}),
    }
}

/** Signaler un retard. Aucun rattrapage : l'étape est masquée dans le formulaire legacy. */
export function lateFlagBody(args: {
    session: AttendanceFlagSession
    minutes: number
    studentEmail?: string | null
}): ApiAttendanceFlagBody {
    return {
        type: "late",
        late_minutes: String(args.minutes),
        occurrence_date: occurrenceDateParam(args.session.date),
        ...(args.studentEmail ? { student_email: args.studentEmail } : {}),
    }
}

/** Paramètres d'URL de `recovery-possibilities`, dans l'ordre du legacy. */
export function recoveryPossibilitiesParams(args: {
    session: Pick<UILiveSession, "date" | "liveSessionId">
    studentEmail?: string | null
}): { student_email?: string; live_session_id?: string | number; occurrence_date: string } {
    return {
        ...(args.studentEmail ? { student_email: args.studentEmail } : {}),
        live_session_id: args.session.liveSessionId,
        occurrence_date: occurrenceDateParam(args.session.date),
    }
}

/** Une ligne de `possible_live_occurrences` (`LiveOccurrenceStudentResource`). */
export type ApiRecoveryOccurrence = {
    id?: string | number
    date?: string
    /** Durée en HEURES (la colonne vaut 1 par défaut, recalculée seulement à la validation). */
    live_units?: number | null
    tutor?: ApiLiveOccurrenceTutor | null
    is_usual_tutor?: boolean
    students_count?: number
    students_same_class?: boolean
}

/** La réponse de `recovery-possibilities`, servie en 200 comme en 400 / 403. */
export type ApiRecoveryPossibilities = {
    can_recover_absence?: boolean
    can_recover_now?: boolean
    reason_slug?: string | null
    possible_live_occurrences?: ApiRecoveryOccurrence[] | null
    /**
     * Le quota (`LiveOccurrence::canRecoverAbsence`) : les absences rattrapables de l'élève dans
     * la fenêtre centrée sur la date de la séance, le plafond de sa formule, la fenêtre en mois.
     * Absents quand la séance ne se rattrape pas par nature, et sur un paiement bloqué.
     */
    recovered_absences_in_period?: number | null
    recovered_absences_max_per_period?: number | null
    recovered_absences_window_size_in_months?: number | null
}

export type RecoveryRefusalSlug =
    | "not_biweekly"
    | "delay_too_short"
    | "already_recovery_session"
    | "max_reached"
    | "absence_too_far"
    | "no_availability"
    | "payment_blocked"

/** `cannotRecoverReasons` de `RecoverLiveSessionComponent`, mot pour mot, plus le blocage paiement. */
export const RECOVERY_REFUSAL_MESSAGES: Record<RecoveryRefusalSlug, string> = {
    not_biweekly: "Ce type de séance ne peut pas être rattrapé.",
    delay_too_short: "La session Live est dans moins de 2 heures.",
    already_recovery_session: "La séance sélectionnée est déjà une séance de rattrapage.",
    max_reached: "Le quota d'absences signalées a été atteint.",
    absence_too_far:
        "Aucune séance de rattrapage disponible pour le moment. Vous serez notifiés par email lorsqu'il y aura des séances disponibles.",
    no_availability:
        "Aucune séance de rattrapage disponible pour le moment. Vous serez notifiés par email lorsqu'il y aura des séances disponibles.",
    payment_blocked:
        "Votre accès est suspendu en raison d'un paiement en attente. Veuillez régulariser votre situation pour accéder à cette fonctionnalité.",
}

const REFUSAL_SLUGS = new Set<string>(Object.keys(RECOVERY_REFUSAL_MESSAGES))

/**
 * Le refus porté par la réponse, ou `null` quand des créneaux sont proposés.
 *
 * Même arbre que l'affichage legacy (`RecoverLiveSessionComponent`) : `can_recover_absence` est
 * réputé vrai quand la clé manque (le legacy écrit `?? 1`), et un `can_recover_now` vrai sans
 * aucune occurrence retombe sur `no_availability`.
 */
export function recoveryRefusalSlug(payload: ApiRecoveryPossibilities | null | undefined): RecoveryRefusalSlug | null {
    const canRecoverAbsence = payload?.can_recover_absence ?? true
    const canRecoverNow = payload?.can_recover_now ?? false
    const count = (payload?.possible_live_occurrences ?? []).length

    if (canRecoverAbsence && canRecoverNow && count > 0) return null

    const slug = payload?.reason_slug ?? null
    if (canRecoverAbsence && canRecoverNow) return "no_availability"
    return slug != null && REFUSAL_SLUGS.has(slug) ? (slug as RecoveryRefusalSlug) : null
}

/**
 * Ce que le legacy ajoute sous un refus qui porte sur le rattrapage lui-même
 * (`RecoverLiveSessionComponent`, l'encadré d'avertissement), passé au vouvoiement.
 */
export const RECOVERY_REFUSED_FOR_GOOD_HINT =
    "Vous pouvez tout de même prévenir de l'absence : la séance ne pourra pas être rattrapée."

/**
 * Vrai quand le serveur refuse le rattrapage lui-même, et pas seulement « pas maintenant » :
 * aucun créneau ne viendra plus tard, l'écran ne doit donc pas en promettre un.
 */
export function isRecoveryRefusedForGood(payload: ApiRecoveryPossibilities | null | undefined): boolean {
    return payload?.can_recover_absence === false
}

/** La phrase à montrer pour un refus, ou `null` quand il n'y en a pas. */
export function recoveryRefusalMessage(payload: ApiRecoveryPossibilities | null | undefined): string | null {
    const slug = recoveryRefusalSlug(payload)
    return slug == null ? null : RECOVERY_REFUSAL_MESSAGES[slug]
}

/**
 * Le quota d'absences que l'encadré « Absences » annonce avant de confirmer, ou rien.
 *
 * Port littéral de `RecoverLiveSessionComponent` et de la garde d'`AbsenceQuotaBadge` : un compte
 * manquant vaut 0, une fenêtre manquante 2 mois, et sans plafond (absent, ou 0) il n'y a pas
 * d'encadré.
 */
export function toUIAbsenceQuota(payload: ApiRecoveryPossibilities | null | undefined): UIAbsenceQuota | undefined {
    const limit = payload?.recovered_absences_max_per_period
    if (!limit) return undefined
    // Le drapeau naît `is_recoverable = can_recover_absence` : c'est lui qui dit si l'absence
    // prendra un crédit. Clé manquante = rattrapable, comme `recoveryRefusalSlug` (legacy `?? 1`).
    const recoverable = payload?.can_recover_absence ?? true
    const reason = recoverable ? undefined : NOT_RECOVERED_REASONS[payload?.reason_slug ?? ""]
    return {
        used: payload?.recovered_absences_in_period ?? 0,
        limit,
        windowMonths: payload?.recovered_absences_window_size_in_months ?? 2,
        recoverable,
        ...(reason != null ? { reason } : {}),
    }
}

/** Les motifs de `LiveOccurrence::canRecoverAbsence` que l'encadré sait dire, dans son ordre. */
const NOT_RECOVERED_REASONS: Record<string, UIAbsenceNotRecoveredReason | undefined> = {
    delay_too_short: "tooLate",
    already_recovery_session: "alreadyRecovery",
    max_reached: "reached",
}

/**
 * Le motif que « Quel nouveau créneau ? » pose en encadré au-dessus des créneaux, ou `null`.
 *
 * Seul un refus pour de bon en a un : il dit pourquoi, et l'étape vide dit ce qui reste possible
 * (`RECOVERY_REFUSED_FOR_GOOD_HINT`). Un « pas maintenant » (`no_availability`, `absence_too_far`)
 * n'en a pas : l'étape vide dit déjà ce qui se passe — l'absence se note, le rattrapage revient
 * dans « À faire ». Sa phrase legacy promettait en plus un e-mail à l'ouverture d'un créneau, que
 * le serveur n'envoie pas : seul part un rappel quand le crédit va expirer.
 */
export function moveSlotsRefusalMessage(payload: ApiRecoveryPossibilities | null | undefined): string | null {
    return isRecoveryRefusedForGood(payload) ? recoveryRefusalMessage(payload) : null
}

/** Ce que « Décaler » lit d'une réponse de `recovery-possibilities`, d'un bloc. */
export type MoveRecoveryReadout = {
    slots: UIParentRecoverySlot[]
    /** Le motif d'un refus pour de bon, en encadré au-dessus des créneaux. */
    refusal: string | null
    /** Ce que l'étape vide dit quand aucun créneau ne viendra : promettre un créneau serait faux. */
    emptyMessage?: string
    /** L'encadré « Absences », à l'étape des créneaux comme à la confirmation d'une absence. */
    quota?: UIAbsenceQuota
}

/**
 * Quand l'encadré « Absences » dit pourquoi l'absence ne sera pas rattrapée (quota atteint, déjà un
 * rattrapage, moins de 2 h), le motif ne s'y ajoute pas — le legacy le retirait déjà pour le quota
 * atteint (`reasonSlug === "max_reached" ? null`). Sans plafond servi, il n'y a pas d'encadré, et le
 * motif reste.
 */
export function moveRecoveryReadout(payload: ApiRecoveryPossibilities | null | undefined): MoveRecoveryReadout {
    const quota = toUIAbsenceQuota(payload)
    const quotaSaysIt = quota?.reason != null
    return {
        slots: toUIParentRecoverySlots(payload),
        refusal: quotaSaysIt ? null : moveSlotsRefusalMessage(payload),
        emptyMessage: isRecoveryRefusedForGood(payload) ? RECOVERY_REFUSED_FOR_GOOD_HINT : undefined,
        quota,
    }
}

/** Rien de lu : l'état d'avant la réponse. */
export const NO_MOVE_RECOVERY: MoveRecoveryReadout = moveRecoveryReadout(null)

/**
 * L'échec d'une requête arrive en `{ ...error.response.data, status }` : la réponse 400 / 403 de
 * `recovery-possibilities` enveloppe donc le même objet sous `data`.
 */
export function recoveryPayloadFromError(error: unknown): ApiRecoveryPossibilities | null {
    if (typeof error !== "object" || error === null || !("data" in error)) return null
    const data = (error as { data?: unknown }).data
    return typeof data === "object" && data !== null ? (data as ApiRecoveryPossibilities) : null
}

/**
 * Le refus que porte une lecture en échec, ou `null` pour une vraie panne : le serveur sert aussi
 * ses refus en 400 / 403 (paiement bloqué), et ils se disent comme ceux d'un 200.
 */
export function recoveryRefusalFromError(error: unknown): ApiRecoveryPossibilities | null {
    const payload = recoveryPayloadFromError(error)
    return payload != null && recoveryRefusalSlug(payload) != null ? payload : null
}

/** Ce que dit l'étape des créneaux quand la lecture échoue pour de bon (panne, réseau). */
export const RECOVERY_SLOTS_FALLBACK_ERROR = "Impossible de charger les créneaux. Veuillez réessayer."

/** « Meilleur groupe pour {prénom} » — règle legacy `RecoverLiveSessionComponent`. */
export function isBestRecoveryOption(occurrence: ApiRecoveryOccurrence | null | undefined): boolean {
    return (
        occurrence?.is_usual_tutor === true ||
        (occurrence?.students_count === 1 && occurrence?.students_same_class === true)
    )
}

/**
 * Les créneaux proposés, dans l'ordre du serveur — « Meilleur groupe » est une chip, pas un tri.
 * Une ligne sans identifiant ou sans date est écartée plutôt que rendue à moitié.
 */
export function toUIParentRecoverySlots(payload: ApiRecoveryPossibilities | null | undefined): UIParentRecoverySlot[] {
    return (payload?.possible_live_occurrences ?? [])
        .map((occurrence): UIParentRecoverySlot | null => {
            if (occurrence?.id == null || !occurrence.date) return null
            return {
                id: occurrence.id,
                date: occurrence.date,
                duration: Math.round((occurrence.live_units ?? 1) * 60),
                tutor: occurrence.tutor != null ? toUITutor(occurrence.tutor) : undefined,
                recommended: isBestRecoveryOption(occurrence),
            }
        })
        .filter((slot): slot is UIParentRecoverySlot => slot != null)
}
